import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle, MapPin, ArrowRight } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { WhereWeWork } from "../components/WhereWeWork";
import { useT } from "../context/LangContext";

const Gallery = ({ images, title, label }) => (
  <div className="mt-8" data-testid="project-gallery">
    <h4 className="font-display font-bold tracking-widest text-xs text-forest mb-4">{label}</h4>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {images.map((src, i) => (
        <div key={i} className={`group relative overflow-hidden rounded-xl ${i === 0 ? "col-span-2 md:col-span-2 aspect-[16/10]" : "aspect-square"}`}>
          <img src={src} alt={`${title} ${i + 1}`} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
        </div>
      ))}
    </div>
  </div>
);

const ProjectBlock = ({ p, index, c }) => (
  <section id={p.id} className="scroll-mt-28 border-t border-border py-12 md:py-16" data-testid={`project-${p.id}`}>
    <div className="mx-auto max-w-7xl px-5 md:px-8 grid gap-12 md:grid-cols-12">
      <div className="md:col-span-5">
        <div className="md:sticky md:top-28">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-serif text-5xl text-ochre leading-none">0{index + 1}</span>
            <span className="rounded-full bg-forest/10 px-3 py-1 text-xs font-semibold text-forest">{p.status}</span>
          </div>
          <span className="font-display font-bold tracking-widest text-xs text-terracotta">{p.kicker.toUpperCase()}</span>
          <h2 className="mt-3 font-display font-extrabold tracking-tighter text-3xl md:text-4xl text-ink">{p.title}</h2>
          <p className="mt-3 inline-flex items-center gap-2 text-sm text-stone">
            <MapPin size={16} weight="duotone" className="text-terracotta" /> {p.location}
          </p>
          <p className="mt-6 text-stone leading-relaxed">{p.lead}</p>

          {p.cta && (
            <Link to="/sostienici" data-testid={`contribute-${p.id}`} className="group mt-8 inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 font-semibold text-white transition-colors duration-200 hover:bg-terracotta-dark">
              {c.contribute}
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          )}
        </div>
      </div>

      <div className="md:col-span-7">
        <div className="relative overflow-hidden rounded-[2rem] aspect-square">
          <img src={p.cover} alt={p.title} className="h-full w-full object-cover" />
        </div>

        <div className="mt-8 rounded-2xl border border-border bg-surface p-7 md:p-9">
          <h3 className="font-display font-bold tracking-tight text-xl text-ink mb-5">{c.goalsLabel}</h3>
          <ul className="space-y-4">
            {p.goals.map((g, i) => (
              <li key={i} className="flex gap-3 text-stone leading-relaxed">
                <CheckCircle size={22} weight="duotone" className="mt-0.5 shrink-0 text-forest" />
                <span>{g}</span>
              </li>
            ))}
          </ul>
        </div>

        {p.gallery.length > 0 && <Gallery images={p.gallery} title={p.title} label={c.galleryLabel} />}
      </div>
    </div>
  </section>
);

export default function Progetti() {
  const { t } = useT();
  const c = t.projects;
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [hash]);

  return (
    <>
      <PageHero kicker={c.heroKicker} titleLines={c.heroTitleLines} intro={c.heroIntro} />
      <WhereWeWork c={c.where} />
      <Reveal>
        {c.items.map((p, i) => (
          <ProjectBlock key={p.id} p={p} index={i} c={c} />
        ))}
      </Reveal>
    </>
  );
}
